import api from "../api";
import { readinessTag } from "./integrationsBeta";

export interface CredentialItem {
  id: number;
  connection: number;
  connection_str: string;
  provider_slug: string;
  type: string;
  label: string;
  secret_id: string;
}

export interface SecretDetail {
  credential_id: number | null;
  connection_id: number;
  client_id: number;
  type: string;
  secret_id: string;
  arn?: string;
  description?: string;
  kms_key_id?: string | null;
  tags?: Record<string, string>;
  secret?: Record<string, unknown>;
  version_id?: string;
}

const credTag = (connectionId: number) => ({
  type: "IntegrationCredential" as const,
  id: connectionId,
});

type ListResponse<T> = T[] | { results: T[] };

export const credentialsBetaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCredentials: builder.query<CredentialItem[], number>({
      query: (connectionId) => ({
        url: "/integration-platform/credentials/",
        method: "GET",
        params: { connection: connectionId },
      }),
      transformResponse: (resp: ListResponse<CredentialItem>) =>
        Array.isArray(resp) ? resp : resp.results,
      providesTags: (_result, _err, connectionId) => [credTag(connectionId)],
    }),

    getSecret: builder.query<
      SecretDetail,
      { connectionId: number; type: string }
    >({
      query: ({ connectionId, type }) => ({
        url: `/integration-platform/secrets/connections/${connectionId}/types/${type}/`,
        method: "GET",
      }),
    }),

    getCredentialSecretSchema: builder.query<
      {
        provider: string;
        connection_type: string;
        type: string;
        secret_schema: Record<string, unknown> | null;
      },
      { connectionId: number; type: string }
    >({
      query: ({ connectionId, type }) => ({
        url: "/integration-platform/credentials/secret-schema/",
        method: "GET",
        params: { connection: connectionId, type },
      }),
    }),

    createSecret: builder.mutation<
      unknown,
      {
        connectionId: number;
        type: string;
        secret: Record<string, unknown>;
        label?: string;
        description?: string;
      }
    >({
      query: ({ connectionId, type, secret, label, description }) => ({
        url: "/integration-platform/secrets/",
        method: "POST",
        body: {
          connection_id: connectionId,
          type,
          secret,
          label: label ?? "",
          description: description ?? "",
        },
      }),
      invalidatesTags: (_result, _err, { connectionId }) => [
        credTag(connectionId),
        readinessTag("ALL"),
      ],
    }),

    updateSecret: builder.mutation<
      unknown,
      {
        connectionId: number;
        type: string;
        secret?: Record<string, unknown>;
        label?: string;
      }
    >({
      query: ({ connectionId, type, secret, label }) => {
        const body: Record<string, unknown> = {};
        if (secret !== undefined) body.secret = secret;
        if (label !== undefined) body.label = label;
        return {
          url: `/integration-platform/secrets/connections/${connectionId}/types/${type}/`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _err, { connectionId }) => [
        credTag(connectionId),
        readinessTag("ALL"),
      ],
    }),

    deleteSecret: builder.mutation<
      unknown,
      { connectionId: number; type: string }
    >({
      query: ({ connectionId, type }) => ({
        url: `/integration-platform/secrets/connections/${connectionId}/types/${type}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { connectionId }) => [
        credTag(connectionId),
        readinessTag("ALL"),
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCredentialsQuery,
  useLazyGetSecretQuery,
  useLazyGetCredentialSecretSchemaQuery,
  useCreateSecretMutation,
  useUpdateSecretMutation,
  useDeleteSecretMutation,
} = credentialsBetaApi;
